/**
 * API client for organization management endpoints.
 * Handles organization CRUD, location management, and version history.
 */

import type {
  CreateLocationData,
  CreatePricingProfileData,
  LocationVersion,
  Organization,
  OrganizationLocation,
  OrganizationLocationDetail,
  PricingProfile,
  UpdateLocationData,
  UpdateOrganizationData,
  UpdatePricingProfileData,
} from '../types'

import { getApiBaseUrl, getApiVersion } from '../../../shared/api/runtimeConfig'
import i18n from '../../../i18n'

const API_URL = getApiBaseUrl()
const API_VERSION = getApiVersion()

export class HttpError extends Error {
  status: number
  data?: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.data = data
  }
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token')
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

function extractErrorMessage(errorData: unknown): string | null {
  if (!errorData || typeof errorData !== 'object') return null

  const anyData = errorData as Record<string, unknown>

  if (typeof anyData.detail === 'string' && anyData.detail.trim()) {
    return anyData.detail
  }

  if (typeof anyData.message === 'string' && anyData.message.trim()) {
    return anyData.message
  }

  // DRF field errors
  for (const value of Object.values(anyData)) {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return value.filter((v) => typeof v === 'string').join(', ')
    }
  }

  return null
}

/**
 * Fetches organization details.
 */
export async function fetchOrganization(
  id: number,
  options: { signal?: AbortSignal } = {},
): Promise<Organization> {
  const url = `${API_URL}/${API_VERSION}/organizations/${id}/`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    if (response.status === 404) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.organization_not_found'), 404, errorData)
    }

    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_fetch_organization', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Updates organization profile.
 */
export async function updateOrganization(
  id: number,
  data: UpdateOrganizationData,
  options: { signal?: AbortSignal } = {},
): Promise<Organization> {
  const url = `${API_URL}/${API_VERSION}/organizations/${id}/`
  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    if (response.status === 403) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.you_do_not_have_permission'),
        403,
        errorData,
      )
    }

    if (response.status === 404) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.organization_not_found'), 404, errorData)
    }

    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_update_organization', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Fetches all locations belonging to an organization.
 */
export async function fetchOrganizationLocations(
  id: number,
  options: { signal?: AbortSignal } = {},
): Promise<OrganizationLocation[]> {
  const url = `${API_URL}/${API_VERSION}/organizations/${id}/locations/`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    if (response.status === 404) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.organization_not_found'), 404, errorData)
    }

    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_fetch_organization_locations', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Fetches a single location belonging to an organization (for edit form).
 * @param locationType - Optional type (refuge, camping, etc.) to ensure we fetch the correct model when IDs can overlap across tables.
 */
export async function fetchOrganizationLocation(
  organizationId: number,
  locationId: number,
  options: { signal?: AbortSignal; locationType?: string } = {},
): Promise<OrganizationLocationDetail> {
  const params = new URLSearchParams()
  if (options.locationType) {
    params.set('type', options.locationType)
  }
  const query = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/${locationId}/${query ? `?${query}` : ''}`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    if (response.status === 403) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.you_do_not_have_permission_2'),
        403,
        errorData,
      )
    }

    if (response.status === 404) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.organization_or_location_not_found'),
        404,
        errorData,
      )
    }

    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_fetch_location', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Creates a new location for an organization.
 */
export async function createOrganizationLocation(
  organizationId: number,
  data: CreateLocationData,
  options: { signal?: AbortSignal } = {},
): Promise<OrganizationLocationDetail> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/`
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    if (response.status === 403) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.you_do_not_have_permission_3'),
        403,
        errorData,
      )
    }

    if (response.status === 404) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.organization_not_found'), 404, errorData)
    }

    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_create_location', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Updates a location belonging to an organization.
 * @param options.locationType - Optional type (refuge, camping, etc.) when IDs can overlap.
 */
export async function updateOrganizationLocation(
  organizationId: number,
  locationId: number,
  data: UpdateLocationData,
  options: { signal?: AbortSignal; locationType?: string } = {},
): Promise<OrganizationLocationDetail> {
  const params = new URLSearchParams()
  if (options.locationType) params.set('type', options.locationType)
  const query = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/${locationId}/${query ? `?${query}` : ''}`
  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    if (response.status === 403) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.you_do_not_have_permission_4'),
        403,
        errorData,
      )
    }

    if (response.status === 404) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.organization_or_location_not_found'),
        404,
        errorData,
      )
    }

    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_update_location', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Duplicate a location (creates a draft copy).
 * Returns the new location detail. Photos/galleries are not copied.
 */
export async function duplicateOrganizationLocation(
  organizationId: number,
  locationId: number,
  options: { signal?: AbortSignal; locationType?: string } = {},
): Promise<OrganizationLocationDetail> {
  const params = new URLSearchParams()
  if (options.locationType) params.set('type', options.locationType)
  const query = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/${locationId}/duplicate/${query ? `?${query}` : ''}`
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    if (response.status === 403) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.you_do_not_have_permission_5'), 403, errorData)
    }
    if (response.status === 404) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.organization_or_location_not_found'), 404, errorData)
    }
    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_duplicate_location', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

/**
 * Deletes a location belonging to an organization.
 * @param options.locationType - Optional type (refuge, camping, etc.) when IDs can overlap.
 */
export async function deleteOrganizationLocation(
  organizationId: number,
  locationId: number,
  options: { signal?: AbortSignal; locationType?: string } = {},
): Promise<void> {
  const params = new URLSearchParams()
  if (options.locationType) params.set('type', options.locationType)
  const query = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/${locationId}/${query ? `?${query}` : ''}`
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    if (response.status === 403) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.you_do_not_have_permission_6'),
        403,
        errorData,
      )
    }

    if (response.status === 404) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.organization_or_location_not_found'),
        404,
        errorData,
      )
    }

    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_delete_location', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }
}

/**
 * Fetches version history for a location.
 */
export async function fetchLocationVersions(
  organizationId: number,
  locationId: number,
  options: { signal?: AbortSignal } = {},
): Promise<LocationVersion[]> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/${locationId}/versions/`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    if (response.status === 404) {
      throw new HttpError(
        msg || i18n.t('organizations:organizationApi.organization_or_location_not_found'),
        404,
        errorData,
      )
    }

    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_fetch_location_versions', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Upload a photo to the primary gallery of a refuge or camping.
 * Creates the primary gallery if needed. Photo is auto-approved.
 * @param options.locationType - 'camping' for camping, default refuge
 */
export async function uploadPrimaryGalleryPhoto(
  organizationId: number,
  locationId: number,
  payload: { image: File; caption?: string },
  options: { signal?: AbortSignal; locationType?: string } = {},
): Promise<{ id: number; image_url: string | null; caption: string; order: number }> {
  const params = new URLSearchParams()
  if (options.locationType) params.set('type', options.locationType)
  const query = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/${locationId}/primary-gallery/photos/${query ? `?${query}` : ''}`
  const formData = new FormData()
  formData.append('image', payload.image)
  if (payload.caption) formData.append('caption', payload.caption)

  const headers: HeadersInit = {}
  const token = localStorage.getItem('access_token')
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    if (response.status === 403) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.you_do_not_have_permission_7'), 403, errorData)
    }
    if (response.status === 404) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.organization_or_location_not_found'), 404, errorData)
    }
    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_upload_photo', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

/**
 * Upload a photo to the organization's own primary gallery.
 */
export async function uploadOrganizationPrimaryGalleryPhoto(
  organizationId: number,
  payload: { image: File; caption?: string },
  options: { signal?: AbortSignal } = {},
): Promise<{ id: number; image_url: string | null; caption: string; order: number }> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/primary-gallery/photos/`
  const formData = new FormData()
  formData.append('image', payload.image)
  if (payload.caption) formData.append('caption', payload.caption)

  const headers: HeadersInit = {}
  const token = localStorage.getItem('access_token')
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_upload_photo', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

/**
 * Remove a photo from the primary gallery of a refuge or camping.
 * @param options.locationType - 'camping' for camping, default refuge
 */
export async function deletePrimaryGalleryPhoto(
  organizationId: number,
  locationId: number,
  photoId: number,
  options: { signal?: AbortSignal; locationType?: string } = {},
): Promise<void> {
  const params = new URLSearchParams()
  if (options.locationType) params.set('type', options.locationType)
  const query = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/${locationId}/primary-gallery/photos/${photoId}/${query ? `?${query}` : ''}`
  const headers: HeadersInit = {}
  const token = localStorage.getItem('access_token')
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers,
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    if (response.status === 403) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.you_do_not_have_permission_8'), 403, errorData)
    }
    if (response.status === 404) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.photo_not_found_in_primary'), 404, errorData)
    }
    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_delete_photo', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }
}

export async function deleteOrganizationPrimaryGalleryPhoto(
  organizationId: number,
  photoId: number,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/primary-gallery/photos/${photoId}/`
  const headers: HeadersInit = {}
  const token = localStorage.getItem('access_token')
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers,
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_delete_photo', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }
}

/**
 * Reorder photos in the primary gallery.
 * @param options.locationType - 'camping' for camping, default refuge
 */
export async function reorderPrimaryGalleryPhotos(
  organizationId: number,
  locationId: number,
  photoIds: number[],
  options: { signal?: AbortSignal; locationType?: string } = {},
): Promise<void> {
  const params = new URLSearchParams()
  if (options.locationType) params.set('type', options.locationType)
  const query = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/locations/${locationId}/primary-gallery/photos/reorder/${query ? `?${query}` : ''}`
  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ photo_ids: photoIds }),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    if (response.status === 403) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.you_do_not_have_permission_9'), 403, errorData)
    }
    if (response.status === 404) {
      throw new HttpError(msg || i18n.t('organizations:organizationApi.primary_gallery_not_found'), 404, errorData)
    }
    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_reorder_photos', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }
}

export async function reorderOrganizationPrimaryGalleryPhotos(
  organizationId: number,
  photoIds: number[],
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/primary-gallery/photos/reorder/`
  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ photo_ids: photoIds }),
    signal: options.signal,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new HttpError(
      msg || i18n.t('organizations:organizationApi.failed_to_reorder_photos', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }
}

export type OrganizationFacilityTypeOption = { value: string; label: string }

export type OrganizationFacilityPoiType = {
  id: number
  slug: string
  name: string
  parent_slug?: string | null
  icon_color?: string | null
  icon_type?: string | null
  mdi_icon?: string | null
}

export type EntityPhoto = {
  id: number
  url: string | null
  caption: string | null
  sort_order?: number
  created_at?: string | null
}

export type OrganizationFacility = {
  id: number
  facility_type: string | null
  facility_type_label: string
  poi_type?: OrganizationFacilityPoiType | null
  poi_type_id?: number | null
  type_label?: string
  name: string | null
  notes: string | null
  info_text?: string | null
  summer_price?: string | null
  winter_price?: string | null
  summer_available?: boolean
  winter_available?: boolean
  address?: string | null
  city?: string | null
  postal_code?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
  source_poi_id?: number | null
  legacy_poi_id?: number | null
  marker_scale?: number
  location: { lat: number | null; lng: number | null }
  photos?: EntityPhoto[]
  created_at?: string | null
  updated_at?: string | null
}

export type RateTagOption = {
  slug: string
  label: string
  is_base?: boolean
  is_active?: boolean
  id?: number | null
}

export type OrganizationPitch = {
  id: number
  organization_id: number | null
  camping_id: number | null
  name: string | null
  notes: string | null
  rate_tag: string | null
  rate_tag_label?: string | null
  location: { lat: number | null; lng: number | null }
  photos?: EntityPhoto[]
  legacy_camping_id?: number | null
  created_at?: string | null
  updated_at?: string | null
}

export type OrganizationRelais = {
  id: number
  organization_id: number | null
  name: string | null
  notes: string | null
  location: { lat: number | null; lng: number | null }
  amenities?: Record<string, string | null>
  photos?: EntityPhoto[]
  legacy_refuge_id?: number | null
  created_at?: string | null
  updated_at?: string | null
}

function authHeadersNoContentType(): HeadersInit {
  const headers: HeadersInit = {}
  const token = localStorage.getItem('access_token')
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

async function uploadEntityPhoto(
  url: string,
  payload: { image: File; caption?: string },
  options: { signal?: AbortSignal } = {},
): Promise<EntityPhoto> {
  const formData = new FormData()
  formData.append('image', payload.image)
  if (payload.caption) formData.append('caption', payload.caption)
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeadersNoContentType(),
    body: formData,
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.upload_photo_failed', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

async function deleteEntityPhoto(
  url: string,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.delete_photo_failed', { status: response.status }),
      response.status,
      errorData,
    )
  }
}

export async function fetchOrganizationFacilities(
  organizationId: number,
  options: { signal?: AbortSignal } = {},
): Promise<{ results: OrganizationFacility[]; facility_types: OrganizationFacilityTypeOption[] }> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/facilities/`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_fetch_facilities', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function createOrganizationFacility(
  organizationId: number,
  data: {
    facility_type?: string
    poi_type_id?: number
    name?: string
    notes?: string
    info_text?: string
    summer_price?: string | number | null
    winter_price?: string | number | null
    location: { lat: number; lng: number }
  },
  options: { signal?: AbortSignal } = {},
): Promise<OrganizationFacility> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/facilities/`
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_create_facility', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function transferPoiToOrganizationFacility(
  organizationId: number,
  poiId: number,
  options: { signal?: AbortSignal } = {},
): Promise<OrganizationFacility> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/facilities/from-poi/`
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ poi_id: poiId }),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_transfer_poi', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function transferOrganizationFacilityToPoi(
  organizationId: number,
  facilityId: number,
  options: { signal?: AbortSignal } = {},
): Promise<{ id: number; name: string; type: string }> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/facilities/${facilityId}/to-poi/`
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_transfer_facility', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function patchOrganizationFacility(
  organizationId: number,
  facilityId: number,
  data: Partial<{
    facility_type: string | null
    poi_type_id: number | null
    name: string | null
    notes: string | null
    info_text: string | null
    summer_price: string | number | null
    winter_price: string | number | null
    location: { lat: number; lng: number }
  }>,
  options: { signal?: AbortSignal } = {},
): Promise<OrganizationFacility> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/facilities/${facilityId}/`
  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_update_facility', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function deleteOrganizationFacility(
  organizationId: number,
  facilityId: number,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/facilities/${facilityId}/`
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_delete_facility', { status: response.status }),
      response.status,
      errorData,
    )
  }
}

export async function uploadOrganizationFacilityPhoto(
  organizationId: number,
  facilityId: number,
  payload: { image: File; caption?: string },
  options: { signal?: AbortSignal } = {},
): Promise<EntityPhoto> {
  return uploadEntityPhoto(
    `${API_URL}/${API_VERSION}/organizations/${organizationId}/facilities/${facilityId}/photos/`,
    payload,
    options,
  )
}

export async function deleteOrganizationFacilityPhoto(
  organizationId: number,
  facilityId: number,
  photoId: number,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  return deleteEntityPhoto(
    `${API_URL}/${API_VERSION}/organizations/${organizationId}/facilities/${facilityId}/photos/${photoId}/`,
    options,
  )
}

export async function fetchOrganizationPitches(
  organizationId: number,
  options: { camping_id?: number; signal?: AbortSignal } = {},
): Promise<{ results: OrganizationPitch[]; rate_tags: RateTagOption[] }> {
  const params = new URLSearchParams()
  if (options.camping_id != null) params.set('camping_id', String(options.camping_id))
  const q = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/pitches/${q ? `?${q}` : ''}`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_fetch_pitches', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function createOrganizationPitch(
  organizationId: number,
  data: {
    name?: string
    notes?: string
    rate_tag?: string
    camping_id?: number | null
    location: { lat: number; lng: number }
  },
  options: { signal?: AbortSignal } = {},
): Promise<OrganizationPitch> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/pitches/`
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_create_pitch', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function deleteOrganizationPitch(
  organizationId: number,
  pitchId: number,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/pitches/${pitchId}/`
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_delete_pitch', { status: response.status }),
      response.status,
      errorData,
    )
  }
}

export async function uploadOrganizationPitchPhoto(
  organizationId: number,
  pitchId: number,
  payload: { image: File; caption?: string },
  options: { signal?: AbortSignal } = {},
): Promise<EntityPhoto> {
  return uploadEntityPhoto(
    `${API_URL}/${API_VERSION}/organizations/${organizationId}/pitches/${pitchId}/photos/`,
    payload,
    options,
  )
}

export async function deleteOrganizationPitchPhoto(
  organizationId: number,
  pitchId: number,
  photoId: number,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  return deleteEntityPhoto(
    `${API_URL}/${API_VERSION}/organizations/${organizationId}/pitches/${pitchId}/photos/${photoId}/`,
    options,
  )
}

export async function fetchOrganizationRelais(
  organizationId: number,
  options: { signal?: AbortSignal } = {},
): Promise<{ results: OrganizationRelais[] }> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/relais/`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_fetch_relais', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function createOrganizationRelais(
  organizationId: number,
  data: {
    name?: string
    notes?: string
    location: { lat: number; lng: number }
    toilet?: string | null
    drinking_water?: string | null
    picnic_table?: string | null
    wifi?: string | null
  },
  options: { signal?: AbortSignal } = {},
): Promise<OrganizationRelais> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/relais/`
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_create_relais', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function deleteOrganizationRelais(
  organizationId: number,
  relaisId: number,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/relais/${relaisId}/`
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_delete_relais', { status: response.status }),
      response.status,
      errorData,
    )
  }
}

export async function uploadOrganizationRelaisPhoto(
  organizationId: number,
  relaisId: number,
  payload: { image: File; caption?: string },
  options: { signal?: AbortSignal } = {},
): Promise<EntityPhoto> {
  return uploadEntityPhoto(
    `${API_URL}/${API_VERSION}/organizations/${organizationId}/relais/${relaisId}/photos/`,
    payload,
    options,
  )
}

export async function deleteOrganizationRelaisPhoto(
  organizationId: number,
  relaisId: number,
  photoId: number,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  return deleteEntityPhoto(
    `${API_URL}/${API_VERSION}/organizations/${organizationId}/relais/${relaisId}/photos/${photoId}/`,
    options,
  )
}

export type OrganizationSitePlanRow = {
  id: number | string
  name: string
  url: string | null
  download_filename?: string
  overlay?: Record<string, unknown>
  is_visible: boolean
  is_published: boolean
  display_order: number
}

export async function fetchOrganizationSitePlans(
  organizationId: number,
  options: { signal?: AbortSignal } = {},
): Promise<{
  site_plans: OrganizationSitePlanRow[]
  site_plan_url: string | null
  active_plan_id: number | string | null
}> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/site-plan/`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_fetch_site_plans', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function updateOrganizationSitePlanMeta(
  organizationId: number,
  planId: number,
  data: {
    name?: string
    is_published?: boolean
    is_visible?: boolean
    display_order?: number
  },
  options: { signal?: AbortSignal } = {},
): Promise<{
  site_plans: OrganizationSitePlanRow[]
}> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/site-plan/`
  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ plan_id: planId, ...data }),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_update_site_plan', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

/**
 * Profils tarifaires d'organisation (catalogue camping | refuge).
 */
export async function listPricingProfiles(
  organizationId: number,
  options: {
    applies_to?: 'camping' | 'refuge' | string
    include_ancestors?: boolean
    nested?: boolean
    signal?: AbortSignal
  } = {},
): Promise<PricingProfile[]> {
  const params = new URLSearchParams()
  if (options.applies_to) params.set('applies_to', options.applies_to)
  if (options.include_ancestors === false) params.set('include_ancestors', '0')
  else if (options.include_ancestors === true) params.set('include_ancestors', '1')
  if (options.nested) params.set('nested', '1')
  const query = params.toString()
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/pricing-profiles/${query ? `?${query}` : ''}`
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_list_pricing_profiles', { status: response.status }),
      response.status,
      errorData,
    )
  }
  const data = await response.json()
  return Array.isArray(data) ? data : []
}

export async function createPricingProfile(
  organizationId: number,
  data: CreatePricingProfileData,
  options: { signal?: AbortSignal } = {},
): Promise<PricingProfile> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/pricing-profiles/`
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_create_pricing_profile', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function updatePricingProfile(
  organizationId: number,
  profileId: number,
  data: UpdatePricingProfileData,
  options: { signal?: AbortSignal } = {},
): Promise<PricingProfile> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/pricing-profiles/${profileId}/`
  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_update_pricing_profile', { status: response.status }),
      response.status,
      errorData,
    )
  }
  return response.json()
}

export async function deletePricingProfile(
  organizationId: number,
  profileId: number,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const url = `${API_URL}/${API_VERSION}/organizations/${organizationId}/pricing-profiles/${profileId}/`
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    signal: options.signal,
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      extractErrorMessage(errorData) || i18n.t('organizations:organizationApi.failed_to_delete_pricing_profile', { status: response.status }),
      response.status,
      errorData,
    )
  }
}
